// Types and helpers for the consultant workspace API (/consultant/workspace,
// backend routes/consultantWorkspace.routes.ts).

import { theme } from "../../theme";

export type Scope = "mine" | "firm";
export type AssignmentRole = "lead" | "supporting" | "reviewer";

export const ASSIGNMENT_ROLE_LABELS: Record<AssignmentRole, string> = {
  lead: "Lead",
  supporting: "Supporting",
  reviewer: "Reviewer",
};

export interface Assignee { userId: string; displayName: string; role: AssignmentRole }

export interface StaffMember {
  id: string;
  displayName: string;
  email: string;
  primaryGrade: string | null;
  consultantRole: string | null;
  isOrgAdmin: boolean;
  clients: Array<{ organizationId: string; name: string; role: AssignmentRole }>;
  leadCount: number;
  assignedCount: number;
  bookedHoursThisMonth: number;
  pendingRequests: number;
}

export type HealthBand = "healthy" | "watch" | "at_risk" | "unassessed";
export interface ClientHealth { health: number | null; band: HealthBand; reasons: string[]; scoreChange30d: number | null }

export const HEALTH_STYLE: Record<HealthBand, { label: string; color: string; bg: string }> = {
  healthy:    { label: "Healthy",    color: theme.color.successText, bg: theme.color.successBg },
  watch:      { label: "Watch",      color: theme.color.warningText, bg: theme.color.warningBg },
  at_risk:    { label: "At risk",    color: theme.color.dangerText,  bg: theme.color.dangerBg },
  unassessed: { label: "Unassessed", color: theme.color.subtle,      bg: theme.color.panelAlt },
};

export interface PipelineRow {
  organizationId: string;
  organizationName: string;
  frameworkId: string;
  bureauName: string | null;
  stage: string;
  stageLabel: string;
  nextDate: string | null;
  nextLabel: string | null;
}
export interface RenewalRow { organizationId: string; organizationName: string; date: string; title: string; frameworkId: string | null; source: "certification" | "calendar" }

export const PIPELINE_ORDER = ["preparing", "engaged", "audit_booked", "certified", "surveillance_due", "renewal_due", "overdue"] as const;
export const PIPELINE_STYLE: Record<string, { color: string; bg: string }> = {
  preparing:        { color: theme.color.subtle,      bg: theme.color.panelAlt },
  engaged:          { color: theme.color.accentText,  bg: theme.color.accentBg },
  audit_booked:     { color: theme.color.auditor,     bg: theme.color.auditorBg },
  certified:        { color: theme.color.successText, bg: theme.color.successBg },
  surveillance_due: { color: theme.color.warningText, bg: theme.color.warningBg },
  renewal_due:      { color: theme.color.warningText, bg: theme.color.warningBg },
  overdue:          { color: theme.color.dangerText,  bg: theme.color.dangerBg },
};

export interface EngagementNote {
  id: string;
  kind: "call" | "meeting" | "decision" | "follow_up" | "note";
  body: string;
  occurredAt: string;
  followUpAt: string | null;
  followUpDoneAt: string | null;
  createdAt: string;
  authorUserId: string | null;
  authorName: string | null;
}

export const NOTE_KIND_LABELS: Record<EngagementNote["kind"], string> = {
  call: "Call", meeting: "Meeting", decision: "Decision", follow_up: "Follow-up", note: "Note",
};

export interface TimeEntry {
  id: string;
  organizationId: string;
  organizationName: string;
  userId: string;
  userName: string;
  date: string;
  hours: number;
  billable: boolean;
  description: string;
}

export interface UtilisationRow {
  id: string;
  displayName: string;
  primaryGrade: string | null;
  capacityHours: number;
  bookedHours: number;
  loggedBillableHours: number;
  loggedOtherHours: number;
  utilisation: number | null;
}

export interface RevenueReport {
  months: Array<{ month: string; bookedPence: number; loggedPence: number }>;
  forecast: Array<{ month: string; confirmedPence: number; pendingPence: number }>;
  byClient: Array<{ id: string; name: string; pence: number }>;
  byConsultant: Array<{ id: string; name: string; pence: number }>;
  byGrade: Array<{ grade: string; label: string; pence: number }>;
}

export const todayKey = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const addDaysKey = (k: string, n: number): string => {
  const [y, m, d] = k.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

export const errText = (e: unknown, fallback: string): string => (e instanceof Error && e.message ? e.message : fallback);

export const monthLabel = (k: string): string =>
  new Date(`${k}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
